import type { GridColDef, GridInitialState } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type {
  ContractType,
  PermissionName,
} from "../../../clients/graphqlTypes.js";
import { useProjectQuoteContext } from "../../main-routes/projects/ProjectQuoteContext.js";
import { ProjectQuoteDataGrid_ItemFragment } from "../../main-routes/projects/ProjectQuoteDataGrid.generated.js";
import { createDecisionWizardButtonsColumn } from "../doc-data-grid/columns/decisionWizardButtonsColumn.js";
import { createTemplatePlaceholderColumn } from "../doc-data-grid/columns/templatePlaceholderColumn.js";
import type { DocDataGridRow } from "../doc-data-grid/types.js";
import { useDocColumns } from "../doc-data-grid/useDocColumns.js";

interface Options {
  projectId: string;
  docId: string;
  contractType: ContractType | undefined;
  canEdit: boolean;
  itemCanBeEdited?: (item: ProjectQuoteDataGrid_ItemFragment) => boolean;
  expandedItemIds: string[] | undefined;
  setItemExpanded?: (id: string, expanded: boolean) => void;
  viewerIsContractor: boolean;
  quote: {
    id: string;
    owningSystemOrganisationId: string;
    viewerPermissions: PermissionName[];
  };
}

export function useQuoteColumns(options: Options): {
  columns: GridColDef<DocDataGridRow<ProjectQuoteDataGrid_ItemFragment>>[];
  initialState: GridInitialState;
} {
  const {
    projectId,
    docId,
    canEdit,
    itemCanBeEdited,
    expandedItemIds,
    viewerIsContractor,
    quote,
  } = options;

  // Ref-based expand callback — avoids columns useMemo dependency on expandedStore
  const setItemExpandedRef = React.useRef(options.setItemExpanded);
  setItemExpandedRef.current = options.setItemExpanded;

  const onTemplatePlaceholderCompleted = React.useCallback((itemId: string) => {
    setItemExpandedRef.current?.(itemId, true);
  }, []);

  const { refetchWizardQuestions } = useProjectQuoteContext();

  const {
    contingencyCheckboxColumn,
    decisionOptionColumn,
    decisionBehaviorColumn,
    descriptionColumn,
    productColumns,
    calculationColumns,
    initialState,
  } = useDocColumns<ProjectQuoteDataGrid_ItemFragment>({
    projectId,
    docId,
    canEdit,
    itemCanBeEdited,
    expandedItemIds,
    viewerIsContractor,
  });

  const { t } = useTranslate(["QuoteItem"]);

  const columns = React.useMemo(
    (): GridColDef<DocDataGridRow<ProjectQuoteDataGrid_ItemFragment>>[] => [
      contingencyCheckboxColumn,
      decisionOptionColumn,

      // Quote-only: template placeholder button
      createTemplatePlaceholderColumn({
        headerName: t("Template placeholder", { ns: "QuoteItem" }),
        canEdit,
        projectId,
        docId,
        refetchWizardQuestions,
        onCompleted: onTemplatePlaceholderCompleted,
      }),

      // Quote-only: decision wizard buttons
      createDecisionWizardButtonsColumn({
        projectId,
        quote,
        canEdit,
        headerName: t("Wizard button", { ns: "QuoteItem" }),
      }),

      decisionBehaviorColumn,
      descriptionColumn,
      ...productColumns,
      ...calculationColumns,
    ],
    [
      contingencyCheckboxColumn,
      decisionOptionColumn,
      decisionBehaviorColumn,
      descriptionColumn,
      productColumns,
      calculationColumns,
      t,
      canEdit,
      projectId,
      docId,
      quote,
      refetchWizardQuestions,
      onTemplatePlaceholderCompleted,
    ]
  );

  return { columns, initialState };
}
